/* Product page hardening.

   Client, 28 Jul call: the chat button overlaps add to cart, and add to cart
   goes out of bounds.

   Cause. The stock sticky bar decides visibility with an IntersectionObserver at
   threshold 1 on the main product form. A threshold of 1 can only ever fire when
   the whole target fits inside the viewport. Once the buy block grows past the
   viewport, which it does on a phone as soon as the dynamic checkout buttons and
   the quantity row are both present, the ratio never reaches 1, the bar is told to
   show and it never hides again. That is the bar sitting on top of the gallery
   with the real add to cart still visible underneath it.

   Fix. Drive it from scroll position against the real add to cart button, which
   cannot fail on a tall target, and hand the measured bar height to CSS so the
   Shopify Inbox launcher can sit above it instead of on top of it. */

(() => {
  const READY = () => {
    const bar = document.querySelector('.sticky-atc-bar');
    if (!bar) return;

    const inner = bar.querySelector('sticky-atc-bar');
    const productId = inner?.getAttribute('data-product-id');
    const form = document.querySelector(
      productId ? `.main-product-form[data-product-id="${productId}"]` : '.main-product-form'
    );
    if (!form) return;

    // Retire the stock observer so the two do not fight over the class.
    if (inner?.observer) {
      try { inner.observer.disconnect(); } catch { /* already gone */ }
    }

    let ticking = false;
    const update = () => {
      ticking = false;
      // Show only once the real buy button has actually left the top of the viewport.
      const past = form.getBoundingClientRect().bottom < 80;
      bar.classList.toggle('sticky-atc-bar--show', past);
      // Omkar, 2 Oct: the floating bar leaves once the footer is on screen (Third Biome's sticky buy does the
      // same) and returns on the way back up. A class, so the theme's own observer is not fought.
      const foot = document.querySelector('.shopify-section-group-footer-group, footer');
      const away = !!foot && foot.getBoundingClientRect().top < window.innerHeight;
      bar.classList.toggle('anyra-atc-away', away);

      const height = bar.classList.contains('sticky-atc-bar--show') && !away ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--anyra-sticky-atc-height', `${height}px`);

      // Omkar, 29 Sep: "move that chat button above ATC, it's overlapping". Before the sticky bar
      // shows, the page's own add to cart scrolls through the corner the chat bubble sits in.
      // While that button is in the bottom band, lift the bubble to ride just above it.
      let lift = 0;
      const atc = form.querySelector('[name="add"]');
      if (atc && !past) {
        const r = atc.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.top < vh && r.bottom > vh - 96) lift = Math.round(vh - r.top + 4);
      }
      document.documentElement.style.setProperty('--anyra-chat-lift', `${lift}px`);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    // The stock bar script can still show the bar on its own timing; follow the bar's actual
    // state so the chat bubble always clears it, whichever script toggled it.
    new MutationObserver(() => {
      const h = bar.classList.contains('sticky-atc-bar--show') ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--anyra-sticky-atc-height', `${h}px`);
    }).observe(bar, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // The bar height changes when a variant with a longer title is picked.
    new ResizeObserver(onScroll).observe(bar);
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', READY);
  } else {
    READY();
  }
})();

/* Anyra, 2 Oct: stars beside the Judge.me average, filled to the real figure (4.7 fills 94 percent). Judge.me
   renders late and re-renders, so this watches for the number and draws once per render. */
(() => {
  const draw = () => {
    document.querySelectorAll('.jm-average-rating-display').forEach((box) => {
      if (box.querySelector('.anyra-avg-stars')) return;
      const num = [...box.querySelectorAll('p')].map((p) => parseFloat(p.textContent)).find((n) => n > 0 && n <= 5);
      if (!num) return;
      const s = document.createElement('span');
      s.className = 'anyra-avg-stars';
      s.setAttribute('aria-hidden', 'true');
      s.style.setProperty('--pct', `${(num / 5) * 100}%`);
      const first = box.querySelector('p');
      first.parentElement.insertBefore(s, first);
    });
  };
  // 6 Oct speed pass: batched to one run per frame instead of one per DOM change
  let drawQueued = false;
  new MutationObserver(() => { if (drawQueued) return; drawQueued = true; requestAnimationFrame(() => { drawQueued = false; draw(); }); }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', draw);
})();

/* Anyra, 3 Oct (Atharva: "add a swipe feature, the offers can continue scrolling automatically"; Omkar: the free
   gift row in the side cart the same). Each row is a real swipeable scroller; every 3s it moves one item on and
   returns to the start at the end. A touch, a drag or a wheel holds it for 6s. Reduced motion: no auto-advance.
   The side cart re-renders its body, so new rows are picked up as they appear and old timers stop. */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const ROWS = [
    ['.anyra-offers__rail', '.anyra-offers__row'],
    ['cart-drawer .anyra-gpick__tier--choose .anyra-gpick__options', ':scope > li'],
  ];
  const start = (rail, itemSel) => {
    if (rail.dataset.anyraAuto) return;
    rail.dataset.anyraAuto = '1';
    let hold = 0;
    const pause = () => { hold = Math.max(hold, Date.now() + 6000); };
    ['touchstart', 'pointerdown', 'wheel'].forEach((ev) => rail.addEventListener(ev, pause, { passive: true }));
    // 15 Oct: a mouse resting on the gift row (or its arrows) holds it, so a card never slides away from the cursor.
    // Real mouse on a fine-pointer screen only: a phone tap fires a compatibility mouseenter with no mouseleave,
    // which would stop the row for good (the 2 Oct bug). The Offers row keeps its live behaviour.
    let hovering = false;
    const gift = rail.closest('.anyra-gpick__rail');
    if (gift) {
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      gift.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && fine.matches) hovering = true; });
      gift.addEventListener('pointerleave', () => { if (hovering) { hovering = false; hold = Math.max(hold, Date.now() + 1500); } });
    }
    const timer = setInterval(() => {
      if (!rail.isConnected) { clearInterval(timer); return; }
      if (hovering || Date.now() < hold || rail.scrollWidth <= rail.clientWidth + 4) return;
      const item = rail.querySelector(itemSel);
      if (!item) return;
      const gap = parseFloat(getComputedStyle(item.parentElement).columnGap) || 10;
      const step = item.getBoundingClientRect().width + gap;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + step, behavior: 'smooth' });
    }, 3000);
  };
  const scan = () => ROWS.forEach(([sel, item]) => document.querySelectorAll(sel).forEach((el) => start(el, item)));
  document.addEventListener('DOMContentLoaded', scan);
  let scanQueued = false;
  new MutationObserver(() => { if (scanQueued) return; scanQueued = true; requestAnimationFrame(() => { scanQueued = false; scan(); }); }).observe(document.documentElement, { childList: true, subtree: true });
})();
